import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaProjectDiagram, 
  FaPlus, 
  FaSearch, 
  FaFilter,
  FaEye,
  FaEdit,
  FaTrash,
  FaDownload,
  FaUsers,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaUserPlus,
  FaTimes,
  FaSave,
  FaCheck,
  FaExclamationTriangle
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageTitle = styled.h1`
  color: #141340;
  font-size: 2rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #F68B21 0%, transparent 100%);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(246, 139, 33, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover {
            background: #c82333;
            transform: translateY(-2px);
          }
        `;
      default:
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
    }
  }}
`;

const SearchAndFilters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;
  
  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #F68B21;
      box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
    }
  }
  
  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #F68B21;
    box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
  }
  
  th {
    background: #f8f9fa;
    color: #141340;
    font-weight: 600;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  tr:hover {
    background: #f8f9fa;
  }
  
  td {
    color: #333;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${({ status }) => {
    switch (status) {
      case 'in-progress':
        return `
          background: #fff3cd;
          color: #856404;
        `;
      case 'completed':
        return `
          background: #d4edda;
          color: #155724;
        `;
      case 'draft':
        return `
          background: #e2e3e5;
          color: #383d41;
        `;
      case 'on-hold':
        return `
          background: #f8d7da;
          color: #721c24;
        `;
      default:
        return `
          background: #e2e3e5;
          color: #383d41;
        `;
    }
  }}
`;

const ActionButtonsCell = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button<{ variant: 'view' | 'edit' | 'delete' | 'assign' }>`
  padding: 0.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${({ variant }) => {
    switch (variant) {
      case 'view':
        return `
          background: #e3f2fd;
          color: #1976d2;
          &:hover {
            background: #bbdefb;
            transform: scale(1.1);
          }
        `;
      case 'edit':
        return `
          background: #fff3e0;
          color: #f57c00;
          &:hover {
            background: #ffe0b2;
            transform: scale(1.1);
          }
        `;
      case 'delete':
        return `
          background: #ffebee;
          color: #d32f2f;
          &:hover {
            background: #ffcdd2;
            transform: scale(1.1);
          }
        `;
      case 'assign':
        return `
          background: #e8f5e8;
          color: #2e7d32;
          &:hover {
            background: #c8e6c9;
            transform: scale(1.1);
          }
        `;
    }
  }}
`;

const ProjectInfo = styled.div`
  .project-name {
    font-weight: 600;
    color: #141340;
    margin-bottom: 0.25rem;
  }
  
  .project-id {
    font-size: 0.8rem;
    color: #666;
  }
  
  .project-description {
    font-size: 0.9rem;
    color: #666;
    margin-top: 0.25rem;
  }
`;

const BudgetInfo = styled.div`
  text-align: right;
  
  .budget-amount {
    font-weight: 600;
    color: #141340;
    font-size: 1.1rem;
  }
  
  .budget-spent {
    font-size: 0.8rem;
    color: #666;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
`;

const PaginationInfo = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PaginationButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid #e0e0e0;
  background: ${({ active }) => (active ? '#F68B21' : 'white')};
  color: ${({ active }) => (active ? 'white' : '#141340')};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ active }) => (active ? '#e67c1a' : '#f8f9fa')};
  }
`;

// Modal styles
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e0e0e0;
`;

const ModalTitle = styled.h3`
  color: #141340;
  font-size: 1.3rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8f9fa;
    color: #141340;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #141340;
  margin-bottom: 0.5rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #F68B21;
    box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
`;

const AlertMessage = styled.div<{ type: 'success' | 'error' | 'warning' }>`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${({ type }) => {
    switch (type) {
      case 'success':
        return `
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        `;
      case 'error':
        return `
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        `;
      case 'warning':
        return `
          background: #fff3cd;
          color: #856404;
          border: 1px solid #ffeaa7;
        `;
    }
  }}
`;

const HRProjectsList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error' | 'warning', message: string } | null>(null);
  const itemsPerPage = 10;

  // Mock data for projects
  const projectsData = [
    {
      id: 'PRJ-001',
      name: 'Road Construction Project',
      description: 'Construction of main highway connecting two major cities',
      
      status: 'in-progress',
      budget: 5000000,
      spent: 3200000,
      startDate: '2023-01-15',
      endDate: '2024-06-30',
      location: 'Dar es Salaam - Arusha',
      type: 'Infrastructure',
      assignedStaff: ['John Doe', 'Jane Smith', 'Mike Wilson']
    },
    {
      id: 'PRJ-002',
      name: 'School Renovation',
      description: 'Renovation of primary school facilities',
      
      status: 'completed',
      budget: 1200000,
      spent: 1180000,
      startDate: '2023-03-01',
      endDate: '2023-12-15',
      location: 'Mwanza',
      type: 'Education',
      assignedStaff: ['Emily Davis', 'Robert Brown']
    },
    {
      id: 'PRJ-003',
      name: 'Water Supply System',
      description: 'Installation of water supply system for rural community',
      
      status: 'draft',
      budget: 2500000,
      spent: 0,
      startDate: '2024-02-01',
      endDate: '2024-12-31',
      location: 'Dodoma',
      type: 'Infrastructure',
      assignedStaff: []
    },
    {
      id: 'PRJ-004',
      name: 'Healthcare Center',
      description: 'Construction of community healthcare center',
      
      status: 'on-hold',
      budget: 3500000,
      spent: 800000,
      startDate: '2023-08-01',
      endDate: '2024-08-31',
      location: 'Mbeya',
      type: 'Healthcare',
      assignedStaff: ['Lisa Anderson', 'Tom Johnson']
    },
    {
      id: 'PRJ-005',
      name: 'Agricultural Training',
      description: 'Training program for local farmers on modern techniques',
      
      status: 'in-progress',
      budget: 800000,
      spent: 450000,
      startDate: '2023-11-01',
      endDate: '2024-05-31',
      location: 'Morogoro',
      type: 'Agriculture',
      assignedStaff: ['Sarah Johnson', 'Mike Wilson']
    }
  ];

  // Mock staff data with roles
  const staffData = [
    { name: 'John Doe', role: 'Supervisor', email: 'john.doe@example.com', phone: '+255 123 456 789', employeeId: 'EMP-001' },
    { name: 'Jane Smith', role: 'Project Manager', email: 'jane.smith@example.com', phone: '+255 123 456 790', employeeId: 'EMP-002' },
    { name: 'Mike Wilson', role: 'Store-Keeper (Logistics)', email: 'mike.wilson@example.com', phone: '+255 123 456 791', employeeId: 'EMP-003' },
    { name: 'Emily Davis', role: 'Cashier', email: 'emily.davis@example.com', phone: '+255 123 456 792', employeeId: 'EMP-004' },
    { name: 'Robert Brown', role: 'Procurement', email: 'robert.brown@example.com', phone: '+255 123 456 793', employeeId: 'EMP-005' },
    { name: 'Lisa Anderson', role: 'Supervisor', email: 'lisa.anderson@example.com', phone: '+255 123 456 794', employeeId: 'EMP-006' },
    { name: 'Tom Johnson', role: 'Project Manager', email: 'tom.johnson@example.com', phone: '+255 123 456 795', employeeId: 'EMP-007' },
    { name: 'Sarah Johnson', role: 'Store-Keeper (Logistics)', email: 'sarah.johnson@example.com', phone: '+255 123 456 796', employeeId: 'EMP-008' },
    { name: 'David Wilson', role: 'Cashier', email: 'david.wilson@example.com', phone: '+255 123 456 797', employeeId: 'EMP-009' },
    { name: 'Michael Smith', role: 'Procurement', email: 'michael.smith@example.com', phone: '+255 123 456 798', employeeId: 'EMP-010' }
  ];

  // Filter and search logic
  const filteredProjects = projectsData.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    const matchesType = typeFilter === 'all' || project.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProjects = filteredProjects.slice(startIndex, endIndex);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getProgressPercentage = (spent: number, budget: number) => {
    return Math.round((spent / budget) * 100);
  };

  const handleAssignStaff = (project: any) => {
    setSelectedProject(project);
    setSelectedStaff('');
    setIsAssignModalOpen(true);
  };

  const handleAssignSubmit = async () => {
    if (!selectedStaff) {
      setAlertMessage({ type: 'error', message: 'Please select a staff member to assign.' });
      return;
    }

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setAlertMessage({ type: 'success', message: `${selectedStaff} has been successfully assigned to ${selectedProject.name}.` });
      
      // Close modal after success
      setTimeout(() => {
        setIsAssignModalOpen(false);
        setAlertMessage(null);
      }, 2000);
    } catch (error) {
      setAlertMessage({ type: 'error', message: 'Failed to assign staff member. Please try again.' });
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        setAlertMessage({ type: 'success', message: 'Project deleted successfully.' });
        
        // Clear alert after 3 seconds
        setTimeout(() => setAlertMessage(null), 3000);
      } catch (error) {
        setAlertMessage({ type: 'error', message: 'Failed to delete project. Please try again.' });
      }
    }
  };

  return (
    <Container>
      {alertMessage && (
        <AlertMessage type={alertMessage.type}>
          <IconWrapper icon={alertMessage.type === 'success' ? FaCheck : FaExclamationTriangle} />
          {alertMessage.message}
        </AlertMessage>
      )}

      <PageHeader>
        <PageTitle>
          <IconWrapper icon={FaProjectDiagram} />
          Projects Overview
        </PageTitle>
        <ActionButtons>
          <Button variant="secondary">
            <IconWrapper icon={FaDownload} />
            Export
          </Button>
        </ActionButtons>
      </PageHeader>

      <SearchAndFilters>
        <SearchInput>
          <IconWrapper icon={FaSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search projects by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>
        <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="in-progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="on-hold">On Hold</option>
        </FilterSelect>
        <FilterSelect value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All Types</option>
          <option value="Infrastructure">Infrastructure</option>
          <option value="Education">Education</option>
          <option value="Healthcare">Healthcare</option>
          <option value="Agriculture">Agriculture</option>
          <option value="Technology">Technology</option>
        </FilterSelect>
      </SearchAndFilters>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Project</th>
              <th>Status</th>
              <th>Budget</th>
              <th>Progress</th>
              <th>Assigned Staff</th>
              <th>Timeline</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentProjects.map((project) => (
              <tr key={project.id}>
                <td>
                  <ProjectInfo>
                    <div className="project-name">{project.name}</div>
                    <div className="project-id">{project.id}</div>
                    <div className="project-description">{project.description}</div>
                  </ProjectInfo>
                </td>

                <td>
                  <StatusBadge status={project.status}>
                    {project.status.replace('-', ' ')}
                  </StatusBadge>
                </td>
                <td>
                  <BudgetInfo>
                    <div className="budget-amount">{formatCurrency(project.budget)}</div>
                    <div className="budget-spent">
                      Spent: {formatCurrency(project.spent)}
                    </div>
                  </BudgetInfo>
                </td>
                <td>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                        {getProgressPercentage(project.spent, project.budget)}%
                      </span>
                      <span style={{ fontSize: '0.8rem', color: '#666' }}>
                        {formatCurrency(project.spent)} / {formatCurrency(project.budget)}
                      </span>
                    </div>
                    <div style={{ 
                      width: '100%', 
                      height: '8px', 
                      backgroundColor: '#e0e0e0', 
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${getProgressPercentage(project.spent, project.budget)}%`,
                        height: '100%',
                        backgroundColor: '#F68B21',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                </td>
                <td>
                  <div style={{ fontSize: '0.9rem' }}>
                    {project.assignedStaff.length > 0 ? (
                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                          {project.assignedStaff.length} staff assigned
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                          {project.assignedStaff.slice(0, 2).join(', ')}
                          {project.assignedStaff.length > 2 && ` +${project.assignedStaff.length - 2} more`}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#666', fontStyle: 'italic' }}>No staff assigned</span>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <IconWrapper icon={FaCalendarAlt} style={{ color: '#666' }} />
    <div>
                      <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                        {new Date(project.startDate).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        to {new Date(project.endDate).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <ActionButtonsCell>
                    <ActionButton variant="view" title="View Details" as={Link} to={`/hr/projects/${project.id}`}>
                      <IconWrapper icon={FaEye} />
                    </ActionButton>
                    <ActionButton variant="edit" title="Edit Project" as={Link} to={`/hr/projects/edit/${project.id}`}>
                      <IconWrapper icon={FaEdit} />
                    </ActionButton>
                    <ActionButton variant="assign" title="Assign Staff" onClick={() => handleAssignStaff(project)}>
                      <IconWrapper icon={FaUserPlus} />
                    </ActionButton>
                    <ActionButton variant="delete" title="Delete Project" onClick={() => handleDeleteProject(project.id)}>
                      <IconWrapper icon={FaTrash} />
                    </ActionButton>
                  </ActionButtonsCell>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        
        <Pagination>
          <PaginationInfo>
            Showing {startIndex + 1} to {Math.min(endIndex, filteredProjects.length)} of {filteredProjects.length} projects
          </PaginationInfo>
          <PaginationButtons>
            <PaginationButton 
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </PaginationButton>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <PaginationButton
                key={page}
                active={page === currentPage}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </PaginationButton>
            ))}
            <PaginationButton 
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </PaginationButton>
          </PaginationButtons>
        </Pagination>
      </TableContainer>

      {/* Assign Staff Modal */}
      <ModalOverlay isOpen={isAssignModalOpen} onClick={() => setIsAssignModalOpen(false)}>
        <Modal onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              <IconWrapper icon={FaUserPlus} />
              Assign Staff to Project
            </ModalTitle>
            <CloseButton onClick={() => setIsAssignModalOpen(false)}>
              <IconWrapper icon={FaTimes} />
            </CloseButton>
          </ModalHeader>
          
          {selectedProject && (
            <>
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                <strong>Project:</strong> {selectedProject.name} ({selectedProject.id})
                <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                  <strong>Available Staff:</strong> {staffData.filter(staff => !selectedProject.assignedStaff.includes(staff.name)).length} members
                </div>
              </div>
              
              <FormGroup>
                <Label>Select Staff Member</Label>
                <Select
                  value={selectedStaff}
                  onChange={(e) => setSelectedStaff(e.target.value)}
                >
                  <option value="">Choose a staff member...</option>
                  {staffData
                    .filter(staff => !selectedProject.assignedStaff.includes(staff.name))
                    .map(staff => (
                      <option key={staff.employeeId} value={staff.name}>
                        {staff.name} - {staff.role}
                      </option>
                    ))
                  }
                </Select>
                {selectedStaff && (
                  <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#f0f8ff', borderRadius: '4px', fontSize: '0.8rem' }}>
                    <strong>Selected Staff:</strong> {selectedStaff}
                    {staffData.find(staff => staff.name === selectedStaff) && (
                      <div style={{ marginTop: '0.25rem', color: '#666' }}>
                        Role: {staffData.find(staff => staff.name === selectedStaff)?.role}
                      </div>
                    )}
    </div>
                )}
              </FormGroup>
              
              <ModalActions>
                <Button variant="secondary" onClick={() => setIsAssignModalOpen(false)}>
                  <IconWrapper icon={FaTimes} />
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleAssignSubmit}>
                  <IconWrapper icon={FaSave} />
                  Assign Staff
                </Button>
              </ModalActions>
            </>
          )}
        </Modal>
      </ModalOverlay>
    </Container>
  );
};

export default HRProjectsList; 